/**
 * PackBuilder — custom element racine de la section.
 *
 * Responsabilités uniques :
 *   • Stocker et gérer la sélection dans le localStorage.
 *   • Exposer des méthodes publiques que les blocs enfants appellent.
 *   • Émettre `pack-builder:selection-changed` quand la sélection change.
 *   • Mettre à jour les barres de progression (header + footer).
 *
 * Toute logique UI (onglets, cartes produits, footer, drawer) vit dans le
 * fichier JS du bloc correspondant.
 */
class PackBuilder extends HTMLElement {
  #abortController = null;
  #foundationConfigCache = null;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    const { signal } = (this.#abortController = new AbortController());
    window.addEventListener('storage', this.#onStorage, { signal });
    this.#syncProgressBars();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  // ── Clé de stockage ───────────────────────────────────────────────────────

  get storageKey() {
    const id = this.dataset.sectionId;
    return id ? `pack-builder-selection:${id}` : 'pack-builder-selection';
  }

  // ── Règle brosse obligatoire ──────────────────────────────────────────────

  #getFoundationConfig() {
    if (this.#foundationConfigCache != null) return this.#foundationConfigCache;
    const el = this.querySelector('script[data-pack-foundation-config]');
    try {
      const parsed = JSON.parse(el?.textContent?.trim() || '{}');
      const variantIds = Array.isArray(parsed?.variantIds) ? parsed.variantIds.map((id) => String(id)) : [];
      const productTitles = Array.isArray(parsed?.productTitles)
        ? parsed.productTitles.filter((title) => typeof title === 'string' && title.trim() !== '')
        : [];
      this.#foundationConfigCache = {
        variantIds,
        productTitles,
        message: typeof parsed?.message === 'string' ? parsed.message : '',
      };
    } catch {
      this.#foundationConfigCache = { variantIds: [], productTitles: [], message: '' };
    }
    return this.#foundationConfigCache;
  }

  isFoundationRuleEnabled() {
    if (!this.hasAttribute('data-pack-foundation-enabled')) return false;
    return this.#getFoundationConfig().variantIds.length > 0;
  }

  #selectionHasVariantFromIds(lines, variantIds) {
    if (!variantIds.length) return true;
    return lines.some((line) => {
      const qty = Math.max(0, parseInt(line.quantity, 10) || 0);
      return qty > 0 && variantIds.includes(String(line.variantId));
    });
  }

  hasFoundationProduct() {
    if (!this.isFoundationRuleEnabled()) return true;
    const { variantIds } = this.#getFoundationConfig();
    const { lines } = this.getSelection();
    return this.#selectionHasVariantFromIds(lines, variantIds);
  }

  getFoundationMessage() {
    return this.#getFoundationConfig().message || '';
  }

  getFoundationProductTitles() {
    return this.#getFoundationConfig().productTitles || [];
  }

  // ── API publique — lecture ────────────────────────────────────────────────

  getSelection() {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return { lines: [] };
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.lines) ? { lines: parsed.lines } : { lines: [] };
    } catch {
      return { lines: [] };
    }
  }

  // ── API publique — écriture ───────────────────────────────────────────────

  addVariant(variantId) {
    const sel = this.getSelection();
    const vid = String(variantId);
    const existing = sel.lines.find((l) => String(l.variantId) === vid);
    if (existing) {
      existing.quantity += 1;
    } else {
      sel.lines.push({ variantId: vid, quantity: 1 });
    }
    this.#save(sel);
    return true;
  }

  setVariantQty(variantId, quantity) {
    const sel = this.getSelection();
    const q = Math.max(0, parseInt(quantity, 10) || 0);
    const vid = String(variantId);

    if (q === 0) {
      sel.lines = sel.lines.filter((l) => String(l.variantId) !== vid);
    } else {
      const existing = sel.lines.find((l) => String(l.variantId) === vid);
      if (existing) {
        existing.quantity = q;
      } else {
        sel.lines.push({ variantId: vid, quantity: q });
      }
    }

    this.#save(sel);
    return true;
  }

  clearSelection() {
    this.#save({ lines: [] });
  }

  // ── Interne ───────────────────────────────────────────────────────────────

  #save(selection) {
    window.localStorage.setItem(this.storageKey, JSON.stringify(selection));
    this.#notify();
  }

  #notify() {
    window.dispatchEvent(
      new CustomEvent('pack-builder:selection-changed', {
        detail: {
          sectionId: this.dataset.sectionId,
          hasFoundation: this.hasFoundationProduct(),
          foundationRuleEnabled: this.isFoundationRuleEnabled(),
        },
      }),
    );
    this.#syncProgressBars();
  }

  /** Synchronisation entre onglets navigateur */
  #onStorage = (event) => {
    if (event.key !== this.storageKey && event.key != null) return;
    this.#notify();
  };

  #syncProgressBars() {
    const { lines } = this.getSelection();
    const totalQuantity = lines.reduce(
      (sum, line) => sum + Math.max(0, parseInt(line.quantity, 10) || 0),
      0,
    );
    this.#updateProgressBars(totalQuantity);
  }

  #updateProgressBars(totalQuantity) {
    for (const track of this.querySelectorAll('[data-progress-track]')) {
      const max = parseInt(track.dataset.max, 10) || 0;
      if (max === 0) continue;

      const tierFills = track.querySelectorAll('[data-tier-fill]');

      if (tierFills.length > 0) {
        for (const fill of tierFills) {
          if (!(fill instanceof HTMLElement)) continue;

          const from = parseInt(fill.dataset.tierFrom, 10) || 0;
          const to = parseInt(fill.dataset.tierTo, 10) || 0;
          let pct = 0;

          if (to > from) {
            if (totalQuantity >= to) pct = 100;
            else if (totalQuantity > from) pct = ((totalQuantity - from) / (to - from)) * 100;
          }

          fill.style.width = `${Math.min(pct, 100)}%`;
        }
      } else {
        const fill = track.querySelector('[data-progress-fill]');

        if (fill instanceof HTMLElement) {
          fill.style.width = `${Math.min((totalQuantity / max) * 100, 100)}%`;
        }
      }

      for (const marker of track.querySelectorAll('[data-tier-count]')) {
        marker.classList.toggle('is-reached', totalQuantity >= (parseInt(marker.dataset.tierCount, 10) || 0));
      }
    }
  }
}

if (!customElements.get('pack-builder')) {
  customElements.define('pack-builder', PackBuilder);
}
